import type { FormatDateOptions, IntlShape } from "@formatjs/intl";
import moment from "moment";

export type DateTimeFormatFunction = (
  value: number | string | Date | moment.Moment,
) => string;

export function DateTimeFormatter(intl: IntlShape) {
  const formatDateTime =
    (options: FormatDateOptions) =>
    (value: string | number | Date | moment.Moment | undefined | null) => {
      if (typeof value === "number" || value instanceof Date) {
        return intl.formatDate(value, options);
      } else if (value == null) {
        return "";
      } else if (moment.isMoment(value)) {
        return intl.formatDate(value.toDate(), options);
      } else {
        return intl.formatDate(moment(value).toDate(), options);
      }
    };

  return {
    formatDateTime,
    date: formatDateTime({
      day: "numeric",
      month: "numeric",
      year: "numeric",
    }),
    dateTime: formatDateTime({
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      month: "numeric",
      timeZoneName: "short",
      year: "numeric",
    }),
    timeUtc: formatDateTime({
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    }),
    time: formatDateTime({
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    }),
    hour: formatDateTime({
      hour: "numeric",
    }),
    longDate: formatDateTime({
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
    month: formatDateTime({
      month: "numeric",
    }),
    noTimezoneDateTime: formatDateTime({
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      month: "numeric",
      year: "numeric",
    }),
    noTimezoneDateTimeSeconds: formatDateTime({
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      month: "numeric",
      year: "numeric",
    }),
    shortDate: formatDateTime({
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    timeNoTimezone: formatDateTime({
      hour: "2-digit",
      minute: "2-digit",
    }),
    twoDigitDate: formatDateTime({
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    year: formatDateTime({
      year: "numeric",
    }),
  };
}
export type DateTimeFormatter = ReturnType<typeof DateTimeFormatter>;
